/**
 * feat-338 Question Analysis — class constants, formatters, vocabulary and on-screen
 * copy. Ported from the design's `design/_shared.js` (the class strings there are spec).
 *
 * On-screen copy lives in QA_COPY (grep there, not in the JSX). Threshold numbers are
 * NEVER literals here: every rule string is built from the API's `meta.thresholds`.
 */
import type { LucideIcon } from 'lucide-react';
import { Clock, LogOut, SpellCheck, XCircle } from 'lucide-react';
import type {
  FlagKey,
  IssueCategory,
  QuestionRow,
  QuestionType,
  Thresholds,
} from '../../../../services/questionAnalysisService';

// ---------------- class constants (design/_shared.js `C`) ----------------
export const C = {
  section: 'bg-white rounded-3xl border border-softBorder shadow-sm p-4 md:p-5 mb-5',
  h2: 'font-display font-semibold text-lg leading-tight text-ink',
  sub: 'text-sm text-inkMuted',
  scroll: 'relative w-fit max-w-full overflow-x-auto rounded-2xl border border-softBorder',
  table: 'w-max border-separate border-spacing-0 text-left',
  th: 'bg-cream-dark px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap',
  thFrozen:
    'sticky left-0 z-30 bg-cream-dark px-3 py-2.5 shadow-[1px_0_0_0_rgba(0,0,0,0.08)] text-left text-[11px] font-bold uppercase tracking-wide',
  td: 'px-3 py-2.5 border-t border-softBorder align-top',
  tdNum: 'px-3 py-2.5 border-t border-softBorder align-top text-right whitespace-nowrap',
  tdFrozen: 'sticky left-0 z-20 px-3 py-2.5 border-t border-softBorder align-top shadow-[1px_0_0_0_rgba(0,0,0,0.08)]',
  flagChip:
    'inline-flex items-center gap-1 rounded-full bg-attention-surface text-attention-on text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  flagRule: 'block text-[11px] text-inkMuted leading-snug mt-0.5 whitespace-nowrap',
  typeChip: 'inline-flex items-center rounded-full bg-gray-100 text-inkMuted text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  /** feat-354: neutral identity chip (never attention-*) — same palette as typeChip. */
  copiesChip:
    'inline-flex items-center gap-1 rounded-full bg-gray-100 text-inkMuted text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  editedChip:
    'inline-flex items-center gap-1 rounded-full bg-white text-ink ring-1 ring-inkMuted/40 text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  notEnough: 'inline-flex items-center rounded-full bg-cream-dark text-inkMuted text-[11px] font-bold px-2 py-0.5 whitespace-nowrap',
  dash: 'text-inkMuted',
  suppressHint: 'block text-[11px] text-inkMuted whitespace-nowrap',
  btnSecondary:
    'inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-softBorder bg-white text-ink hover:bg-cream-dark focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-50 disabled:cursor-not-allowed',
  btnInk:
    'inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-lg bg-ink text-white hover:bg-ink/90 focus:outline-none focus:ring-2 focus:ring-coral/40 disabled:opacity-60',
  worse: 'bg-warning-surface',
  select:
    'border border-softBorder rounded-lg px-3 py-2 text-sm bg-white text-ink font-semibold focus:outline-none focus:ring-2 focus:ring-coral/40 max-w-full',
  label: 'flex flex-col gap-1 text-xs font-bold text-inkMuted min-w-0',
  banner: 'rounded-2xl border border-danger-line/40 bg-danger-surface p-3 text-sm text-danger-text flex items-start gap-3',
  bannerBtn:
    'shrink-0 text-xs font-bold px-3 py-1.5 rounded-lg border border-danger-line/40 bg-white text-danger-text hover:bg-danger-em disabled:opacity-60',
  h4: 'text-xs font-bold uppercase tracking-wide text-inkMuted mb-1.5',
} as const;

// ---------------- formatting ----------------

/** Contract fraction → whole percent. null stays null (rendered as a dash by the caller). */
export function pct(f: number | null | undefined): string | null {
  return f == null ? null : `${Math.round(f * 100)}%`;
}

/** The 1-decimal value for a cell's `title` (a 50.4% row is flagged yet shows "50%"). */
export function pctTitle(f: number | null | undefined): string | undefined {
  return f == null ? undefined : `${(f * 100).toFixed(1)}%`;
}

/** Question answer time: seconds, 1 decimal under 10 s, otherwise whole (FRONTEND-SLICE §3). */
export function formatAnswerSeconds(ms: number | null | undefined): string | null {
  if (ms == null) return null;
  const s1 = Math.round(ms / 100) / 10;
  return s1 < 10 ? `${s1.toFixed(1)} s` : `${Math.round(ms / 1000)} s`;
}

/** Lesson time: minutes, 1 decimal. */
export function formatMinutes(ms: number | null | undefined): string | null {
  return ms == null ? null : `${(ms / 60000).toFixed(1)} min`;
}

/** Whole minutes for a target label ("10 min"). */
export function formatTargetMinutes(ms: number): string {
  const m = ms / 60000;
  return `${Number.isInteger(m) ? m : m.toFixed(1)} min`;
}

export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

/**
 * The fallback learner floor, used only until a `/questions` response has supplied
 * `meta.thresholds.suppress_below_learners` (the source of truth).
 */
export const DEFAULT_SUPPRESS_BELOW_LEARNERS = 3;

/** "Oct 26" in the tenant's timezone. */
export function formatShortDate(iso: string, timeZone: string = DEFAULT_TIMEZONE): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
  } catch {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: DEFAULT_TIMEZONE });
  }
}

// ---------------- vocabulary ----------------

export const TYPE_LABEL: Record<QuestionType, string> = {
  multiple_choice: 'Multiple choice',
  fill_blank: 'Fill in the blank',
};

export const LANG_LABEL: Record<string, string> = { en: 'English', es: 'Spanish' };
export const langLabel = (code: string) => LANG_LABEL[code] ?? code;

export interface FlagDef {
  label: string;
  Icon: LucideIcon;
  /** The rule, built from the API thresholds. */
  rule: (t: Thresholds) => string;
}

export const FLAG: Record<FlagKey, FlagDef> = {
  often_missed: {
    label: 'Often missed',
    Icon: XCircle,
    rule: (t) => `more than ${Math.round(t.often_missed_wrong_rate * 100)}% wrong on first try`,
  },
  slow: {
    label: 'Slow',
    Icon: Clock,
    rule: (t) => `typical answer ${t.slow_multiple_of_type_median}× the usual for its type or slower`,
  },
  quit: {
    label: 'People quit here',
    Icon: LogOut,
    rule: (t) => `${Math.round(t.quit_exit_rate * 100)}% or more of lessons ended on it`,
  },
  near_miss: {
    label: 'Near-misses',
    Icon: SpellCheck,
    rule: (t) => `${t.near_miss_rejected_learners}+ learners had a typed answer rejected`,
  },
};
export const FLAG_ORDER: FlagKey[] = ['often_missed', 'slow', 'quit', 'near_miss'];

export const ISSUE_LABEL: Record<IssueCategory, string> = {
  ambiguous: 'Ambiguous wording',
  wrong_or_risky_answer: 'Wrong or risky answer',
  too_hard_for_second_language_readers: 'Hard for second-language readers',
  fitb_answer_too_strict: 'Answer check too strict',
  distractor_problem: 'Problem with the wrong options',
  other: 'Other',
};

// ---------------- "notably worse than all stores" (DESIGN-QUESTION Q2) ----------------
/** Rates: shade when this many percentage points (as a fraction) worse than overall. */
export const WORSE_RATE_POINTS = 0.1;
/** Times: shade when at least this multiple of the overall (25% longer). */
export const WORSE_TIME_RATIO = 1.25;

export type StoreMetricKey =
  | 'first_try_correct_rate'
  | 'median_lesson_active_ms'
  | 'p90_lesson_active_ms'
  | 'lessons_over_target_rate'
  | 'lesson_exit_rate';

/** Worse only; better is never shaded. A display comparison of two API numbers. */
export function isNotablyWorse(key: StoreMetricKey, v: number | null, overall: number | null): boolean {
  if (v == null || overall == null) return false;
  switch (key) {
    case 'first_try_correct_rate':
      return overall - v >= WORSE_RATE_POINTS - 1e-9;
    case 'lessons_over_target_rate':
    case 'lesson_exit_rate':
      return v - overall >= WORSE_RATE_POINTS - 1e-9;
    default:
      return v >= overall * WORSE_TIME_RATIO;
  }
}

/** One shared time-bar scale per table: max(15 min, ceil(max p90 / 5 min) × 5 min). */
export function timeScaleMax(p90s: (number | null)[]): number {
  const vals = p90s.filter((v): v is number => v != null);
  const max = vals.length ? Math.max(...vals) : 0;
  return Math.max(15 * 60000, Math.ceil(max / 300000) * 300000);
}

// ---------------- question sorting ----------------

export type QuestionSortKey =
  | 'default'
  | 'learners'
  | 'attempts'
  | 'first_try_wrong_rate'
  | 'still_wrong_after_retry_rate'
  | 'median_answer_ms'
  | 'p90_answer_ms'
  | 'exit_rate'
  | 'ai_graded_learners'
  | 'rejected_learners';

export interface QuestionSort {
  key: QuestionSortKey;
  dir: 'asc' | 'desc';
}
export const DEFAULT_QUESTION_SORT: QuestionSort = { key: 'default', dir: 'desc' };

export function questionSortValue(r: QuestionRow, key: QuestionSortKey): number | null {
  if (r.suppressed) return null;
  switch (key) {
    case 'learners':
      return r.learners;
    case 'attempts':
      return r.attempts;
    case 'first_try_wrong_rate':
      return r.first_try_wrong_rate;
    case 'still_wrong_after_retry_rate':
      return r.still_wrong_after_retry_rate;
    case 'median_answer_ms':
      return r.median_answer_ms;
    case 'p90_answer_ms':
      return r.p90_answer_ms;
    case 'exit_rate':
      return r.exit_rate;
    case 'ai_graded_learners':
      return r.fitb?.ai_graded_learners ?? null;
    case 'rejected_learners':
      return r.fitb?.rejected_learners ?? null;
    default:
      return null;
  }
}

/** 0 = rankable, 1 = not enough data, 2 = suppressed. Lower buckets always first. */
function bucket(r: QuestionRow): number {
  if (r.suppressed) return 2;
  return r.enough_data ? 0 : 1;
}

/**
 * Client re-sort. Default keeps the server's order. Under any sort, not-enough-data
 * rows stay after rankable rows and suppressed rows last; blanks sort last both ways.
 */
export function sortQuestions(rows: QuestionRow[], sort: QuestionSort): QuestionRow[] {
  const indexed = rows.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => {
    const bk = bucket(a.r) - bucket(b.r);
    if (bk !== 0) return bk;
    if (sort.key === 'default') return a.i - b.i;
    const va = questionSortValue(a.r, sort.key);
    const vb = questionSortValue(b.r, sort.key);
    if (va == null && vb == null) return a.i - b.i;
    if (va == null) return 1;
    if (vb == null) return -1;
    const d = sort.dir === 'asc' ? va - vb : vb - va;
    return d !== 0 ? d : a.i - b.i;
  });
  return indexed.map((x) => x.r);
}

export function nextQuestionSort(cur: QuestionSort, key: QuestionSortKey): QuestionSort {
  if (key === 'default') return DEFAULT_QUESTION_SORT;
  if (cur.key === key) return { key, dir: cur.dir === 'desc' ? 'asc' : 'desc' };
  return { key, dir: 'desc' };
}

// ---------------- copy ----------------

export const QA_COPY = {
  back: 'Administration',
  title: 'Question Analysis',
  intro: (tenant: string, floor: number) =>
    `Which Learn questions to fix first, and whether long lessons are a store problem or a curriculum problem, for ${tenant}. Totals only: no employee names, and any group under ${floor} learners is hidden.`,
  dataStart: 'First-try accuracy available from Oct 25, 2026 (PT).',
  noTenant: 'No tenant selected. Return to a scorecard page and pick a tenant first.',
  deniedMessage: 'This page is only available to Bespoke staff and corporate users.',

  // filters
  filters: 'Filters',
  dates: 'Dates',
  from: 'From',
  to: 'To',
  dateTo: 'to',
  store: 'Store',
  allStores: 'All stores',
  language: 'Language',
  allLanguages: 'All languages',
  questionType: 'Question type',
  allTypes: 'All types',
  lesson: 'Lesson',
  allLessons: 'All lessons',
  flaggedOnly: 'Flagged only',
  includeEarlier: 'Include earlier wordings',
  resetFilters: 'Reset filters',

  // summary
  summary: 'Summary',
  sumLearners: 'Learners',
  sumLessons: 'Lessons completed',
  sumRight: 'Right on first try',
  sumRightFoot: 'across all questions answered',
  sumTypical: 'Typical lesson time',
  sumTypicalFoot: (target: string) => `median · target under ${target}`,
  sumOver: (target: string) => `Lessons over ${target}`,
  sumOverFoot: 'time counted only while the app is open',

  // suppression / blanks
  fewerThan: (floor: number) => `fewer than ${floor} learners`,
  hiddenLabel: (floor: number) => `Hidden: fewer than ${floor} learners`,
  notApplicableMc: 'Not applicable to multiple choice',
  nobodyRetried: 'Nobody needed the end-of-lesson retry',
  /** r3: a null time/rate may be "no rows" OR "under the learner floor"; the API can't tell which. */
  noValue: (floor: number) => `No data, or fewer than ${floor} learners`,

  // questions
  questions: 'Questions',
  questionsSub: (n: number, flagged: number) => ({ count: `${n} question${n === 1 ? '' : 's'}`, flagged: `${flagged} flagged` }),
  questionsOrder: 'flagged first, then most wrong on first try',
  exportCsv: 'Export CSV',
  nothingToExport: 'Nothing to export',
  versionCurrent: (n: number) => ({
    a: 'Counting answers to the current wording only. ',
    b: `${n} answer${n === 1 ? '' : 's'} left out`,
    c: ' because we couldn’t tell which wording they saw. Turn on ',
    d: 'Include earlier wordings',
    e: ' to count them.',
  }),
  versionCurrentNoneLeft: 'Counting answers to the current wording only.',
  versionAll: 'Counting answers to every wording, including before a question was edited.',
  flagKeyLead: (min: number) => `Flagged when at least ${min} learners answered and:`,
  usualTime: (mc: string | null, fb: string | null) =>
    `Usual answer time: multiple choice ${mc ?? '—'}, fill in the blank ${fb ?? '—'}.`,
  tableFoot: 'Click a question to see the full wording, results by store and language, and wrong answers.',
  noFlags: 'No flags',
  notEnoughData: 'Not enough data',
  notEnoughTitle: (min: number) => `Needs answers from at least ${min} learners before it can be flagged`,
  learnersNeeded: (n: number, min: number) => `${n} of ${min} learners needed`,
  edited: (d: string) => `Edited ${d}`,
  editedTitle: (d: string, all: boolean) =>
    `Wording changed ${d}. Only answers to the new wording are counted.${all ? ' Counting all wordings.' : ''}`,
  editedTitleAll: (d: string) => `Wording changed ${d}. Counting all wordings.`,
  mobileLine: (wrong: string | null, flags: number, notEnough: boolean) =>
    `${wrong ?? '—'} wrong 1st try · ${flags ? `${flags} flag${flags > 1 ? 's' : ''}` : notEnough ? 'not enough data' : 'no flags'}`,
  cappedLeftOut: (n: number) => `${n} left out`,
  cappedTitle: (n: number) =>
    `${n} answer${n > 1 ? 's' : ''} hit the time cap (app left open) and ${n > 1 ? 'are' : 'is'} left out of the times`,
  // feat-354 — copy count (the backend rolls the metrics up; React only prints it)
  copies: (n: number) => `original + ${n} ${n === 1 ? 'copy' : 'copies'}`,
  copiesTitle: (n: number) => `Results combine this question and its ${n} ${n === 1 ? 'copy' : 'copies'} in copied lessons.`,
  expand: 'Expand question',
  collapse: 'Collapse question',
  defaultOrderTitle: 'Default order: flagged first, then most wrong',

  // states
  loading: 'Loading question results…',
  emptyTitle: 'No Learn answers recorded yet in this range.',
  emptyDetail: 'First-try accuracy is available from Oct 25, 2026.',
  showAllDates: 'Show all dates',
  noMatchSub: '0 questions match these filters',
  noMatchTitle: 'No questions match these filters.',
  noMatchFlagged: { a: 'With ', b: 'Flagged only', c: ' on, this means nothing crossed a flag rule. That is good news, not missing data.' },
  questionsFailed: 'Couldn’t load question results.',
  timeFailed: 'Couldn’t load lesson times.',
  timeFailedDetail: 'The questions above are unaffected.',
  failedDetail: (status: number | null) =>
    status
      ? `Something went wrong on our side (error ${status}). Your filters are kept; try again in a moment.`
      : 'Something went wrong. Your filters are kept; try again in a moment.',
  tryAgain: 'Try again',
} as const;

/** Copy for the expanded question panel (design 02). */
export const QA_DETAIL_COPY = {
  label: 'Question details',
  loading: 'Loading question details…',
  failed: 'Couldn’t load this question’s details.',
  fullQuestion: (type: string) => `Full question · ${type}`,
  acceptedAnswers: 'Accepted answers:',
  options: 'Options:',
  correct: 'correct',
  explanation: 'Explanation shown:',
  lesson: 'Lesson:',
  /** feat-354 */
  copies: 'Copies:',
  copiesTail: '· results include every copy',
  openInLearn: 'Open in Learn Module',
  openInLearnTitle: (lesson: string) => `Opens the Learn Module. Find this question in the lesson “${lesson}”.`,
  suppressedNote: (floor: number) =>
    `Fewer than ${floor} learners have answered this question in this range, so its results are hidden.`,
  whyFlagged: 'Why it’s flagged',
  rule: 'rule:',
  howEnded: (n: number) => `How the ${n} answers ended`,
  mix: {
    pass_first: 'Right first try',
    pass_mc_partial: 'Typed answer wrong, multiple-choice fallback right',
    pass_retry: 'Wrong, then right on the retry',
    fail: 'Still wrong after the retry',
  },
  stillWrongLead: 'Still wrong after retry: ',
  stillWrongTail: ' of the answers that went to the retry. A high number here usually means the question, not the learner.',
  stillWrongNone: 'Nobody needed the end-of-lesson retry.',
  byStore: 'By store',
  byLanguage: 'By language',
  colStore: 'Store',
  colLanguage: 'Language',
  colLearners: 'Learners',
  colWrong: 'Wrong 1st try',
  colTime: 'Typical time',
  noBreakdown: 'No answers in this range.',
  rejectedTitle: 'Most common rejected answers',
  rejectedSub: 'Typed answers the AI check said no to, grouped ignoring case and spaces. Top 10.',
  rejectedNone: 'No typed answers were rejected in this range.',
  acceptedTitle: 'Accepted after AI check',
  acceptedSub: 'Not an exact match, but the AI said yes. Consider adding these as accepted answers. Top 5.',
  acceptedNone: 'None in this range.',
  reasonLead: 'AI’s reason (example): ',
  learnersN: (n: number) => `${n} learner${n === 1 ? '' : 's'}`,
  footer: (floor: number) => `No names are shown. Groups under ${floor} learners are hidden.`,
  actual: {
    often_missed: (wrong: string) => `${wrong} wrong on first try`,
    slow: (med: string, usual: string) => `typical answer ${med} (usual for its type ${usual})`,
    quit: (exit: string) => `${exit} of lessons ended on it`,
    near_miss: (n: number | null, floor: number) =>
      n == null ? `fewer than ${floor} learners had a typed answer rejected` : `${n} learners had a typed answer rejected`,
  },
} as const;

/** Column headers for the Questions table (design `questionHead()`). */
export const QUESTION_COLUMNS: { key: QuestionSortKey; label: string[]; title: string; minW: string }[] = [
  { key: 'learners', label: ['Learners'], title: 'Different people who answered it', minW: 'min-w-[84px]' },
  { key: 'attempts', label: ['Answers'], title: 'Times it was answered (a person can answer more than once)', minW: 'min-w-[84px]' },
  { key: 'first_try_wrong_rate', label: ['Wrong 1st try'], title: 'Share of answers that were wrong on the first try', minW: 'min-w-[104px]' },
  {
    key: 'still_wrong_after_retry_rate',
    label: ['Still wrong', 'after retry'],
    title: 'Of the answers wrong the first time and retried at the end of the lesson, the share still wrong',
    minW: 'min-w-[112px]',
  },
  { key: 'median_answer_ms', label: ['Typical', 'answer time'], title: 'Median time to answer, first time it was shown', minW: 'min-w-[96px]' },
  { key: 'p90_answer_ms', label: ['1 in 10', 'took over'], title: '90th percentile time to answer: 9 in 10 answered faster than this', minW: 'min-w-[104px]' },
  { key: 'exit_rate', label: ['Quit here'], title: 'Share of lessons that showed this question and were left on it', minW: 'min-w-[84px]' },
  {
    key: 'ai_graded_learners',
    label: ['AI-checked'],
    title: 'Fill in the blank only: learners whose typed answer was not an exact match and went to the AI check',
    minW: 'min-w-[92px]',
  },
  { key: 'rejected_learners', label: ['AI-rejected'], title: 'Fill in the blank only: learners whose typed answer the AI check rejected', minW: 'min-w-[92px]' },
];
